import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, PieChart, Download, Calendar, Filter, DollarSign, Truck } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseAnalysis = () => {
  const [analysisData, setAnalysisData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('last30days');
  const [selectedProject, setSelectedProject] = useState('all');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipmentData, setShipmentData] = useState(null);

  const periods = [
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last90days', label: 'Last 90 Days' },
    { value: 'lastyear', label: 'Last Year' },
  ];

  useEffect(() => {
    fetchProjects();
    fetchAnalysisData();
  }, []);

  useEffect(() => {
    fetchAnalysisData();
  }, [selectedPeriod, selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/projects/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const projectsData = Array.isArray(response.data) ? response.data : 
                          response.data.results ? response.data.results : [];
      const projectOptions = [
        { value: 'all', label: 'All Projects' },
        ...projectsData.map(project => ({ 
          value: project.id, 
          label: project.name 
        }))
      ];
      setProjects(projectOptions);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([{ value: 'all', label: 'All Projects' }]);
    }
  };

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      
      // Fetch inventory summary
      const summaryResponse = await axios.get(`${API_BASE_URL}/api/warehouse/analytics/inventory_summary/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Fetch project usage analytics
      let projectUrl = `${API_BASE_URL}/api/warehouse/analytics/project_usage/`;
      if (selectedProject !== 'all') {
        projectUrl += `?project_id=${selectedProject}`;
      }
      const projectResponse = await axios.get(projectUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Fetch top used items
      const topItemsResponse = await axios.get(`${API_BASE_URL}/api/warehouse/analytics/top_used_items/?limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Fetch shipment data
      let shipmentUrl = `${API_BASE_URL}/api/warehouse/shipments/`;
      if (selectedProject !== 'all') {
        shipmentUrl += `?project=${selectedProject}`;
      }
      const shipmentsResponse = await axios.get(shipmentUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Safely get data arrays
      const projectData = Array.isArray(projectResponse.data) ? projectResponse.data : 
                         projectResponse.data.results ? projectResponse.data.results : [];
      const shipmentsData = Array.isArray(shipmentsResponse.data) ? shipmentsResponse.data : 
                           shipmentsResponse.data.results ? shipmentsResponse.data.results : [];
      const topItemsData = Array.isArray(topItemsResponse.data) ? topItemsResponse.data : 
                          topItemsResponse.data.results ? topItemsResponse.data.results : [];
      
      // Calculate summary statistics
      const totalIssues = projectData.reduce((sum, project) => sum + (project.total_issues || 0), 0);
      const totalProjectCost = projectData.reduce((sum, project) => sum + parseFloat(project.total_value || 0), 0);
      const averageCostPerProject = projectData.length > 0 ? totalProjectCost / projectData.length : 0;
      
      // Calculate shipment statistics
      const totalShipments = shipmentsData.length;
      const totalShipmentValue = shipmentsData.reduce((sum, shipment) => sum + parseFloat(shipment.price || 0), 0);
      const averageShipmentValue = totalShipments > 0 ? totalShipmentValue / totalShipments : 0;

      setAnalysisData({
        summary: {
          totalCost: totalProjectCost,
          totalItems: summaryResponse.data.total_items,
          totalIssues: totalIssues,
          averageCostPerProject: averageCostPerProject,
          totalShipments: totalShipments,
          totalShipmentValue: totalShipmentValue,
          averageShipmentValue: averageShipmentValue
        },
        projectBreakdown: projectData.map(project => ({
          projectName: project.project_name,
          totalCost: parseFloat(project.total_value || 0),
          itemsIssued: project.items_count || 0,
          categories: (project.categories_breakdown || []).map(cat => ({
            category: cat.name,
            cost: parseFloat(cat.total_value || 0),
            items: cat.items_count || 0
          }))
        })),
        categoryTrends: (summaryResponse.data.categories_breakdown || []).map(cat => ({
          category: cat.name,
          thisMonth: parseFloat(cat.total_value || 0),
          lastMonth: parseFloat(cat.total_value || 0) * 0.8, // Mock calculation
          growth: 20 // Mock growth percentage
        })),
        topItems: topItemsData.map(item => ({
          name: item.item_name,
          totalIssued: item.total_issued,
          totalCost: parseFloat(item.total_value_issued || 0),
          projects: item.projects_count
        })),
        shipments: shipmentsData.map(shipment => ({
          id: shipment.id,
          name: shipment.name,
          price: parseFloat(shipment.price || 0),
          projectName: shipment.project_name,
          createdAt: shipment.created_at
        }))
      });

    } catch (error) {
      console.error('Error fetching analysis data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = () => {
    if (!analysisData) return;
    
    // Create CSV content
    let csvContent = "Project,Total Cost,Items Issued,Category,Category Cost,Category Items\n";
    
    analysisData.projectBreakdown.forEach(project => {
      project.categories.forEach(category => {
        csvContent += `"${project.projectName}",${project.totalCost},${project.itemsIssued},"${category.category}",${category.cost},${category.items}\n`;
      });
    });
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `warehouse-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-300 rounded"></div>
              ))}
            </div>
            <div className="space-y-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto pb-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BarChart3 className="mr-3 h-8 w-8 text-blue-600" />
                Warehouse Analysis
              </h1>
              <p className="text-gray-600 mt-2">Analyze inventory usage, costs, and trends per project</p>
            </div>
            <button 
              onClick={generateReport}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
              <div className="relative">
                <Calendar className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {periods.map(period => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="w-full md:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
              <div className="relative">
                <Filter className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {projects.map(project => (
                    <option key={project.value} value={project.value}>
                      {project.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {analysisData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Cost</p>
                <p className="text-3xl font-bold text-gray-900">€{analysisData.summary.totalCost.toLocaleString()}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Items Issued</p>
                <p className="text-3xl font-bold text-gray-900">{analysisData.summary.totalItems}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Issues</p>
                <p className="text-3xl font-bold text-gray-900">{analysisData.summary.totalIssues}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Cost/Project</p>
                <p className="text-3xl font-bold text-gray-900">€{analysisData.summary.averageCostPerProject.toLocaleString()}</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <PieChart className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Shipments</p>
                <p className="text-3xl font-bold text-gray-900">{analysisData.summary.totalShipments}</p>
              </div>
              <div className="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Shipment Value</p>
                <p className="text-3xl font-bold text-gray-900">€{analysisData.summary.totalShipmentValue.toLocaleString()}</p>
              </div>
              <div className="h-12 w-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-cyan-600" />
              </div>
            </div>
          </div>
        </div>
        )}

        {analysisData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Project Breakdown */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost by Project</h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {analysisData.projectBreakdown.map((project, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{project.projectName}</h4>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">€{project.totalCost.toLocaleString()}</p>
                      <p className="text-sm text-gray-500">{project.itemsIssued} items</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {project.categories.map((category, catIndex) => (
                      <div key={catIndex} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{category.category}</span>
                        <div className="text-right">
                          <span className="font-medium">€{category.cost.toLocaleString()}</span>
                          <span className="text-gray-500 ml-2">({category.items} items)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Category Trends */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Trends</h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {analysisData.categoryTrends.map((trend, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{trend.category}</p>
                    <p className="text-sm text-gray-500">This month: €{trend.thisMonth.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${trend.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {trend.growth >= 0 ? '+' : ''}{trend.growth.toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-500">vs last month</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Shipment Analysis */}
        {analysisData && analysisData.shipments && analysisData.shipments.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipment Details</h3>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Shipment Name</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Price</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Project</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Created Date</th>
                </tr>
              </thead>
              <tbody>
                {analysisData.shipments.map((shipment, index) => (
                  <tr key={shipment.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-3 px-4 text-gray-900">{shipment.name}</td>
                    <td className="py-3 px-4 text-gray-900">€{shipment.price.toLocaleString()}</td>
                    <td className="py-3 px-4 text-gray-600">{shipment.projectName || 'No project'}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(shipment.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

      </div>
    </div>
  );
};

export default WarehouseAnalysis;